import * as React from 'react';
import { showGlobalModal } from 'core/components/GlobalModal';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { apiFileUpload } from 'core/utils/fileUpload/api';
import { extractUploadedUrl, getApiErrorMessage } from 'features/DigitalMarketing/utils/apiResponse';

type UploadOptions = {
    preferObjectValue?: boolean;
    useNewUploadFlow?: boolean;
    allowedMimeTypes?: string[];
    maxFileSizeMb?: number;
};

const defaultAllowedMimeTypes = ['image/png', 'image/jpeg', 'image/jpg'];

export const useWebsiteImageUpload = ({
    preferObjectValue = false,
    useNewUploadFlow = false,
    allowedMimeTypes = defaultAllowedMimeTypes,
    maxFileSizeMb = 15,
}: UploadOptions = {}) => {
    const authData = useAuth();
    const [isUploadingImage, setIsUploadingImage] = React.useState(false);
    const previewUrlsRef = React.useRef<string[]>([]);

    React.useEffect(() => () => {
        previewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    }, []);

    const uploadImage = React.useCallback(async (
        file: File,
        onLocalPreview?: (url: string) => void,
    ) => {
        if (!allowedMimeTypes.includes(file.type)) {
            showGlobalModal('Only png, jpg, or jpeg files are allowed', 'error');
            return '';
        }

        if (file.size > maxFileSizeMb * 1024 * 1024) {
            showGlobalModal(`File size exceeds ${maxFileSizeMb}MB`, 'error');
            return '';
        }

        const localPreviewUrl = URL.createObjectURL(file);
        previewUrlsRef.current.push(localPreviewUrl);
        onLocalPreview?.(localPreviewUrl);
        setIsUploadingImage(true);

        try {
            const formData = new FormData();
            formData.append('dest', 'WEBSITE');
            formData.append('branch_id', authData?.BRANCH_ID as any);
            formData.append('database', authData?.DATABASE as any);
            formData.append('files', file);

            const response = await apiFileUpload(formData, 'upload', '', '', true, useNewUploadFlow);
            const imageUrl = extractUploadedUrl(response, preferObjectValue);

            if (!imageUrl) {
                showGlobalModal('Unable to read uploaded image URL', 'error');
                return '';
            }

            return imageUrl;
        } catch (error: any) {
            showGlobalModal(getApiErrorMessage(error, 'Failed to upload image'), 'error');
            return '';
        } finally {
            setIsUploadingImage(false);
        }
    }, [allowedMimeTypes, authData?.BRANCH_ID, authData?.DATABASE, maxFileSizeMb, preferObjectValue, useNewUploadFlow]);

    return {
        isUploadingImage,
        uploadImage,
    };
};
